import React from 'react'

import { UI2FormContextProps, UI2FormContext } from './ui2-form.context'

/**
 * Did the uiSchema node itself set isHorizontalLayout (vs inherit it)? Can't test
 * props.isHorizontalLayout !== undefined — the renderer spreads its context into every
 * component's props, so the prop is ~never undefined; only the uiSchema node holds intent.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function schemaSetsHorizontalLayout(props: any): boolean {
  return props?.frames?.getContext?.('uiSchema')?.isHorizontalLayout !== undefined
}

/**
 * Injects UI2FormContext as props on a UI2 field/section. Child prop wins for the
 * overrideable defaults; context wins when the form force-sets isReadOnly/isDisabled true.
 * Forwards refs so the entity renderer can track the instance for UI-driven validation.
 */
export function withUI2FormContext<P extends Partial<UI2FormContextProps>>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentType<P> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const AnyComponent = WrappedComponent as React.ComponentType<any>
  const displayName = AnyComponent.displayName || AnyComponent.name || 'Component'

  const WithUI2FormContext = React.forwardRef<unknown, P>((props, ref) => (
    <UI2FormContext.Consumer>
      {(context) => {
        const merged: UI2FormContextProps & Record<string, unknown> = {
          ...context,
          ...props,
        }
        if (context.isReadOnly === true) {
          merged.isReadOnly = true
        }
        if (context.isDisabled === true) {
          merged.isDisabled = true
        }
        if (!schemaSetsHorizontalLayout(props) && AnyComponent.defaultProps?.isHorizontalLayout !== undefined) {
          merged.isHorizontalLayout = AnyComponent.defaultProps.isHorizontalLayout
        }
        if (context.inlineHelpText === null) {
          merged.inlineHelpText = null
          merged.isRequired = false
          merged.isOptional = false
        }
        // notApplicable sections: their required fields must not block entity save.
        if (context.sectionIsNotApplicable === true) {
          merged.isRequired = false
        }
        return <AnyComponent {...merged} ref={ref} />
      }}
    </UI2FormContext.Consumer>
  ))

  WithUI2FormContext.displayName = `WithUI2FormContext(${displayName})`
  return WithUI2FormContext as unknown as React.ComponentType<P>
}
